/**
 * Best-effort display formatting for workbook cells.
 *
 * The grid is READ-ONLY, so this is a display convenience, not a fidelity
 * guarantee: we do not reimplement Excel's number-format engine. We answer the
 * two questions that actually change what a bid manager sees — "is this a date
 * hiding as a serial?" and "is this money?" — and pass everything else through.
 *
 * Dates always render in AU order (dd/mm/yyyy) regardless of the workbook's own
 * pattern. The alternative (honouring `mm/dd/yy` from a US-authored template)
 * would show an Australian user an ambiguous date with no way to tell which
 * order they were looking at.
 */

/** Excel's day 0 — 1899-12-30 UTC, which absorbs the 1900 leap-year bug. */
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30)
const MS_PER_DAY = 86_400_000

/** Serials outside this window are almost certainly plain numbers, not dates. */
const MIN_SERIAL = 1
const MAX_SERIAL = 2_958_465 // 9999-12-31

/**
 * Strip the parts of a number format that carry literal text rather than
 * placeholders: quoted runs, backslash escapes, locale/colour brackets, and
 * everything after the first `;` section (positive form is what we render).
 */
function formatSkeleton(numFmt: string): string {
  const positive = numFmt.split(';')[0] ?? numFmt
  return positive
    .replace(/"[^"]*"/g, '')
    .replace(/\\./g, '')
    .replace(/\[[^\]]*\]/g, '')
}

/** Does this number format describe a date/time rather than a number? */
export function isDateNumFmt(numFmt?: string): boolean {
  if (!numFmt) return false
  const skeleton = formatSkeleton(numFmt)
  if (skeleton.includes('%')) return false
  return /[dmyhs]/i.test(skeleton) && !/[#0]/.test(skeleton)
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value)
}

/** Resolve a cell value to a Date, whether it arrived as a serial or ISO text. */
function toDate(value: string | number): Date | null {
  if (typeof value === 'number') {
    if (value < MIN_SERIAL || value > MAX_SERIAL) return null
    return new Date(EXCEL_EPOCH_MS + Math.round(value * MS_PER_DAY))
  }
  // Only ISO-ish strings — a free-text cell ('TBC') must survive untouched.
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function formatDate(date: Date, withTime: boolean): string {
  const day = pad(date.getUTCDate())
  const month = pad(date.getUTCMonth() + 1)
  const stamp = `${day}/${month}/${date.getUTCFullYear()}`
  if (!withTime) return stamp
  return `${stamp} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`
}

/**
 * Leading currency symbol declared by the format, if any. Read from the RAW
 * format, because Excel hides currency inside the locale bracket: `[$$-409]` is
 * "dollar sign, locale 409" while `[$-409]` is locale-only and carries no
 * symbol at all — stripping brackets first would confuse the two.
 */
function currencySymbol(numFmt: string): string {
  const bracketed = /\[\$([^\]-]*)/.exec(numFmt)
  if (bracketed?.[1]) return bracketed[1]
  const match = /[$£€]/.exec(numFmt.replace(/\[[^\]]*\]/g, ''))
  return match ? match[0] : ''
}

function formatNumber(value: number, numFmt: string): string {
  const skeleton = formatSkeleton(numFmt)

  if (skeleton.includes('%')) {
    const decimals = /\.(0+)/.exec(skeleton)?.[1]?.length ?? 0
    return `${(value * 100).toFixed(decimals)}%`
  }

  if (!/[#0]/.test(skeleton)) return String(value)

  const decimals = /\.(0+)/.exec(skeleton)?.[1]?.length ?? 0
  const grouped = skeleton.includes(',')
  const body = value.toLocaleString('en-AU', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: grouped,
  })
  return `${currencySymbol(numFmt)}${body}`
}

/**
 * Render a cell value for display. `numFmt` is the workbook's own format string
 * (absent for unformatted cells) and only ever refines the result — an
 * unrecognised format falls back to the raw value rather than guessing.
 */
export function formatCellValue(
  value: string | number | boolean | null,
  numFmt?: string
): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE'

  if (isDateNumFmt(numFmt)) {
    const date = toDate(value)
    if (date) return formatDate(date, /h/i.test(formatSkeleton(numFmt ?? '')))
    // Not date-shaped after all — fall through and show what is actually there.
  }

  if (typeof value === 'number' && numFmt) return formatNumber(value, numFmt)

  return String(value)
}
